import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import {
  PhoneCall, Brain, Shield, BarChart3, Users, FileText,
  Zap, ArrowRight, CheckCircle2, Headphones
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' } }),
};

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Analysis',
    desc: 'GPT-5.2 analyzes every call against your SOPs, scoring compliance and flagging violations automatically.',
    span: 'md:col-span-8',
  },
  {
    icon: FileText,
    title: 'SOP Management',
    desc: 'Upload PDFs or create checklists with weighted parameters.',
    span: 'md:col-span-4',
  },
  {
    icon: Headphones,
    title: 'Live Monitoring',
    desc: 'Real-time call tracking with cloud telephony integration.',
    span: 'md:col-span-4',
  },
  {
    icon: BarChart3,
    title: 'Rich Reporting',
    desc: 'Interactive dashboards with trends, agent comparisons, and exportable reports.',
    span: 'md:col-span-4',
  },
  {
    icon: Users,
    title: 'Team Management',
    desc: 'Multi-role access with admin controls, teams, and agent performance tracking.',
    span: 'md:col-span-4',
  },
];

const stats = [
  { value: '10x', label: 'Faster Audits' },
  { value: '99.2%', label: 'Accuracy Rate' },
  { value: '50%', label: 'Cost Reduction' },
  { value: '24/7', label: 'Monitoring' },
];

const LandingPage = () => {
  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="landing-page">
      <PublicNav />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505] z-10" />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1770486036751-e55247238964?w=1920&q=60)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          {/* Gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF3366]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FF8C00]/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <motion.div initial="hidden" animate="visible" className="max-w-4xl">
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-6"
            >
              AI-Powered Call Quality Assessment
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-5xl md:text-7xl font-medium tracking-tighter leading-[1.05] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Every call scored.{' '}
              <span className="bg-gradient-to-r from-[#FF3366] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                Every violation caught.
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mb-10"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              CallScript AI transcribes, analyzes, and scores call recordings against your SOPs in seconds — not days. Automate compliance reviews and deliver actionable insights to every team.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              <Link
                to="/login?tab=register"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-8 py-3.5 font-semibold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)]"
                data-testid="hero-signup-btn"
              >
                Get Started Free <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-white px-8 py-3.5 font-semibold hover:bg-white/10 transition-all"
                data-testid="hero-demo-btn"
              >
                Request a Demo
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/5 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-semibold bg-gradient-to-r from-[#FF3366] to-[#FFD700] bg-clip-text text-transparent">
                  {s.value}
                </p>
                <p className="text-sm text-zinc-500 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Capabilities</p>
            <h2
              className="text-4xl md:text-5xl font-medium tracking-tight"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Built for modern call centers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className={`${f.span} rounded-3xl bg-[#0F0F11] border border-white/5 p-8 lg:p-10 group hover:-translate-y-1 transition-transform`}
                  data-testid={`feature-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF3366]/20 to-[#FF8C00]/20 flex items-center justify-center mb-5">
                    <Icon size={24} className="text-[#FF3366]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{f.title}</h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/capabilities"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF3366] hover:text-[#FF8C00] transition-colors"
              data-testid="explore-capabilities-link"
            >
              Explore all capabilities <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">How It Works</p>
            <h2
              className="text-4xl md:text-5xl font-medium tracking-tight"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Three steps to compliance
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Upload', desc: 'Upload call recordings or connect your cloud telephony provider for live monitoring.', icon: PhoneCall },
              { step: '02', title: 'Analyze', desc: 'AI transcribes and scores every call against your custom SOPs and audit sheets.', icon: Brain },
              { step: '03', title: 'Improve', desc: 'Review dashboards, track trends, and share reports to drive continuous improvement.', icon: BarChart3 },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15, duration: 0.5 }}
                  className="relative"
                >
                  <span className="text-6xl font-bold text-white/5 absolute -top-4 left-0">{s.step}</span>
                  <div className="pt-10">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF3366]/20 to-[#FF8C00]/20 flex items-center justify-center mb-4">
                      <Icon size={24} className="text-[#FF8C00]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{s.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#FF3366]/10 via-[#0F0F11] to-[#FF8C00]/10 border border-white/5 p-12 lg:p-20 text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366]/5 to-[#FF8C00]/5 opacity-50" />
            <div className="relative z-10">
              <h2
                className="text-4xl md:text-5xl font-medium tracking-tight mb-4"
                style={{ fontFamily: "'Clash Display', sans-serif" }}
              >
                Ready to transform your call quality?
              </h2>
              <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-8">
                Join teams using CallScript AI to automate compliance reviews and improve agent performance.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/login?tab=register"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-8 py-3.5 font-semibold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)]"
                  data-testid="cta-signup-btn"
                >
                  Start Free <ArrowRight size={18} />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-white px-8 py-3.5 font-semibold hover:bg-white/10 transition-all"
                  data-testid="cta-pricing-btn"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
